import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "../contexts/AuthContext";
import { 
  CheckCircle, 
  ArrowRight,
  Loader2,
  XCircle,
  PartyPopper
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${API}/api`;

// Plan display names
const getPlanDisplayName = (plan) => {
  const planNames = {
    "pro": "Solo",
    "business": "Expert BTP",
    "free": "Découverte"
  };
  return planNames[plan] || plan;
};

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const { getAuthHeader, refreshUsage } = useAuth();
  const navigate = useNavigate();

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/checkout/status/${sessionId}`,
        { headers: getAuthHeader() }
      );

      if (response.data.payment_status === "paid") {
        setPaymentInfo(response.data);
        setStatus("success");
        // Refresh user's usage/plan info
        refreshUsage();
      } else if (response.data.status === "expired") {
        setStatus("error");
      } else {
        // Payment still processing, check again
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center p-6">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-[128px]" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <picture>
              <source srcSet="/logo.webp" type="image/webp" />
              <img 
                src="/logo.png" 
                alt="AURA Gestion - Retour à l'accueil"
                width="48"
                height="48"
                className="w-12 h-12 rounded-xl object-contain"
              />
            </picture>
            <div className="text-left">
              <span className="text-2xl font-bold text-white">AURA</span>
            </div>
          </Link>
        </div>

        <Card className="bg-zinc-900/80 border-zinc-800">
          <CardContent className="pt-8 pb-8 text-center">
            {status === "loading" && (
              <>
                <Loader2 className="w-16 h-16 mx-auto text-violet-500 animate-spin mb-6" />
                <h2 className="text-2xl font-bold mb-2">Vérification du paiement...</h2>
                <p className="text-zinc-400">Merci de patienter quelques instants</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                </div>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <PartyPopper className="w-6 h-6 text-amber-400" />
                  <h2 className="text-2xl font-bold">Paiement réussi !</h2>
                  <PartyPopper className="w-6 h-6 text-amber-400" />
                </div>
                <p className="text-zinc-400 mb-6">
                  Bienvenue dans AURA <span className="text-violet-400 font-semibold">{getPlanDisplayName(paymentInfo?.plan)}</span> !
                </p>

                {paymentInfo && (
                  <div className="p-4 rounded-xl bg-zinc-800/50 mb-6 text-left">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-zinc-400">Plan</span>
                      <span className="font-medium">{getPlanDisplayName(paymentInfo.plan)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-zinc-400">Période</span>
                      <span className="font-medium">
                        {paymentInfo.period === "yearly" ? "Annuel" : "Mensuel"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-400">Montant</span>
                      <span className="font-medium">{paymentInfo.amount}€</span>
                    </div>
                  </div>
                )}

                <Button
                  onClick={() => navigate("/app")}
                  className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 h-12"
                >
                  Accéder à mon espace
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {status === "error" && (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-6">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Paiement non trouvé</h2>
                <p className="text-zinc-400 mb-6">
                  Le paiement a expiré ou n'a pas été complété.
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate("/pricing")}
                    className="w-full bg-violet-600 hover:bg-violet-500 h-12"
                  >
                    Réessayer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full border-zinc-700 hover:bg-zinc-800 h-12"
                  >
                    Retour à l'accueil
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
